import { useEffect, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import { useParams } from 'react-router-dom';

import beaconLogo from 'assets/BeaconLogo.svg';

import { getFeedbackPreview } from 'services/previewShare';

import styles from './styles.module.scss';

const PreviewShare = () => {
  const { postProcessUuid } = useParams();
  const [previewPayload, setPreviewPayload] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!postProcessUuid) {
      setError('Preview link is missing.');
      setIsLoading(false);

      return;
    }

    // eslint-disable-next-line compat/compat
    const abortController = new AbortController();

    const fetchPreview = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const payload = await getFeedbackPreview(postProcessUuid, abortController.signal);

        setPreviewPayload(payload);
      }
      catch (err) {
        if (err.name === 'AbortError') {
          return;
        }

        const apiMessage = err?.response?.data?.message || err?.response?.data?.detail;

        setError(apiMessage || 'Unable to load preview right now.');
      }
      finally {
        setIsLoading(false);
      }
    };

    fetchPreview();

    return () => {
      abortController.abort();
    };
  }, [postProcessUuid]);

  const htmlUploadKey = previewPayload?.post_process?.result_data?.html_upload_info?.key;
  const htmlPreviewUrl = htmlUploadKey
    ? `${process.env.REACT_APP_PREVIEW_CLOUDFRONT_URL}${htmlUploadKey}`
    : null;
  const reviewFrameName = previewPayload?.design_review?.extra_data?.review_frame_name;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <span className={styles.brand}>
          {reviewFrameName || 'onBeacon'}
        </span>

        <a
          href={`${process.env.REACT_APP_ONBEACON_WEBSITE_URL}`}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.madeWith}
        >
          <img
            className={styles.madeWithIcon}
            src={beaconLogo}
            alt="onBeacon logo"
          />
          <span className={styles.madeWithText}>
            Made with onBeacon
          </span>
        </a>
      </header>

      <main className={styles.content}>
        <section className={styles.card}>
          {isLoading && (
            <div className={styles.loader}>
              <ImpulseSpinner frontColor={'#28BDAE'} />
            </div>
          )}

          {!isLoading && error && (
            <div className={styles.message}>
              <h2>
                Something went wrong
              </h2>
              <p>
                {error}
              </p>
            </div>
          )}

          {!isLoading && !error && !htmlPreviewUrl && (
            <div className={styles.message}>
              <h2>
                Preview unavailable
              </h2>
              <p>
                We couldn&apos;t find a preview to show for this link.
              </p>
            </div>
          )}

          {!isLoading && !error && htmlPreviewUrl && (
            <div className={styles.frameWrapper}>
              <iframe
                title="Design preview"
                src={htmlPreviewUrl}
                className={styles.frame}
                allowFullScreen
              />
            </div>
          )}
        </section>
      </main>
    </div>
  );
};

PreviewShare.propTypes = {};

export default PreviewShare;
